function createContributionError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function normalizeSurfaceLifecycle(value) {
  if (typeof value === 'function') return { unmount: value };
  if (value && typeof value === 'object') return value;
  return {};
}

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'ABORT_ERR';
}

export function resolveSettingsContributionProviderManifest({
  providerPluginId = '',
  pluginManifests = []
} = {}) {
  const normalizedProviderId = String(providerPluginId || '').trim();
  if (!normalizedProviderId) {
    throw createContributionError(
      'SETTINGS_CONTRIBUTION_PROVIDER_MISSING',
      'Settings contribution provider is not configured.'
    );
  }
  const manifests = Array.isArray(pluginManifests) ? pluginManifests : [];
  const manifest = manifests.find((entry) => String(entry?.id || '').trim() === normalizedProviderId);
  if (!manifest) {
    throw createContributionError(
      'SETTINGS_CONTRIBUTION_PROVIDER_UNDECLARED',
      `Settings contribution provider '${normalizedProviderId}' is not declared by the active app.`
    );
  }
  if (manifest?.nav?.hidden !== true) {
    throw createContributionError(
      'SETTINGS_CONTRIBUTION_PROVIDER_VISIBLE',
      `Settings contribution provider '${normalizedProviderId}' must be navigation-hidden.`
    );
  }
  return manifest;
}

export function createSettingsContributionRuntime({
  providerPluginId = '',
  pluginManifests = [],
  plugins = null,
  providerContext = {}
} = {}) {
  const surfaces = new Map();
  const pendingControllers = new Map();
  let providerPromise = null;
  let provider = null;
  let providerGeneration = 0;

  async function loadProvider() {
    if (provider) return provider;
    if (providerPromise) return providerPromise;
    const generation = providerGeneration;
    const attempt = Promise.resolve().then(async () => {
      resolveSettingsContributionProviderManifest({ providerPluginId, pluginManifests });
      if (!plugins || typeof plugins.load !== 'function' || typeof plugins.isDeclared !== 'function') {
        throw createContributionError(
          'SETTINGS_CONTRIBUTION_LOADER_UNAVAILABLE',
          'The declared-plugin loader is unavailable for Settings contributions.'
        );
      }
      if (!plugins.isDeclared(providerPluginId)) {
        throw createContributionError(
          'SETTINGS_CONTRIBUTION_PROVIDER_UNDECLARED',
          `Settings contribution provider '${providerPluginId}' is not declared by the active app.`
        );
      }
      const module = await plugins.load(providerPluginId);
      const factory = module && typeof module.createSettingsContribution === 'function'
        ? module.createSettingsContribution
        : null;
      if (!factory) {
        throw createContributionError(
          'SETTINGS_CONTRIBUTION_FACTORY_MISSING',
          `Settings contribution provider '${providerPluginId}' does not export createSettingsContribution().`
        );
      }
      const created = await factory({
        ...providerContext,
        providerPluginId
      });
      if (!created || typeof created !== 'object' || typeof created.mountPanel !== 'function') {
        if (created && typeof created.unmount === 'function') await created.unmount();
        throw createContributionError(
          'SETTINGS_CONTRIBUTION_PROVIDER_INVALID',
          `Settings contribution provider '${providerPluginId}' must return a mountPanel() lifecycle.`
        );
      }
      if (generation !== providerGeneration) {
        if (typeof created.unmount === 'function') await created.unmount();
        const aborted = createContributionError(
          'SETTINGS_CONTRIBUTION_PROVIDER_DISPOSED',
          `Settings contribution provider '${providerPluginId}' was disposed while loading.`
        );
        aborted.name = 'AbortError';
        throw aborted;
      }
      provider = created;
      return provider;
    });
    providerPromise = attempt;
    try {
      return await attempt;
    } catch (error) {
      if (providerPromise === attempt) providerPromise = null;
      throw error;
    }
  }

  function surfaceKey(kind, descriptor) {
    return `${String(kind || '').trim()}:${String(descriptor?.id || '').trim()}`;
  }

  async function unmountSurface(kind, descriptor) {
    const key = surfaceKey(kind, descriptor);
    const pendingController = pendingControllers.get(key);
    if (pendingController) {
      pendingControllers.delete(key);
      pendingController.abort();
    }
    const entry = surfaces.get(key);
    if (!entry) return;
    surfaces.delete(key);
    entry.controller.abort();
    if (typeof entry.lifecycle?.unmount === 'function') {
      await entry.lifecycle.unmount(entry.context);
    }
  }

  async function mountSurface({ kind = 'panel', descriptor, mountEl } = {}) {
    const normalizedKind = kind === 'appearance' ? 'appearance' : 'panel';
    if (!descriptor || typeof descriptor !== 'object' || !String(descriptor.id || '').trim()) {
      throw createContributionError('SETTINGS_CONTRIBUTION_DESCRIPTOR_INVALID', 'Settings contribution descriptor is invalid.');
    }
    if (!mountEl) {
      throw createContributionError('SETTINGS_CONTRIBUTION_HOST_MISSING', `Settings contribution host '${descriptor.id}' is missing.`);
    }
    const key = surfaceKey(normalizedKind, descriptor);
    const current = surfaces.get(key);
    if (current && current.mountEl === mountEl && !current.controller.signal.aborted) {
      if (typeof current.lifecycle?.refresh === 'function') {
        await current.lifecycle.refresh(current.context);
      }
      return current.lifecycle;
    }
    if (current) await unmountSurface(normalizedKind, descriptor);

    const controller = new AbortController();
    pendingControllers.set(key, controller);
    const context = {
      ...providerContext,
      mountEl,
      root: mountEl,
      descriptor: { ...descriptor },
      signal: controller.signal
    };
    let lifecycle = {};
    try {
      const loadedProvider = await loadProvider();
      if (controller.signal.aborted) {
        pendingControllers.delete(key);
        return lifecycle;
      }
      const mountMethod = normalizedKind === 'appearance'
        ? loadedProvider.mountAppearanceSection
        : loadedProvider.mountPanel;
      if (typeof mountMethod !== 'function') {
        throw createContributionError(
          'SETTINGS_CONTRIBUTION_MOUNT_MISSING',
          `Settings contribution provider '${providerPluginId}' cannot mount ${normalizedKind} surfaces.`
        );
      }
      lifecycle = normalizeSurfaceLifecycle(await mountMethod(context));
      pendingControllers.delete(key);
      if (controller.signal.aborted) {
        if (typeof lifecycle.unmount === 'function') await lifecycle.unmount(context);
        return lifecycle;
      }
      surfaces.set(key, { mountEl, controller, context, lifecycle });
      return lifecycle;
    } catch (error) {
      pendingControllers.delete(key);
      controller.abort();
      if (isAbortError(error)) return lifecycle;
      throw error;
    }
  }

  async function unmountAll({ disposeProvider = false } = {}) {
    [...pendingControllers.values()].forEach((controller) => controller.abort());
    pendingControllers.clear();
    const entries = [...surfaces.values()];
    surfaces.clear();
    let firstError = null;
    for (const entry of entries) {
      entry.controller.abort();
      if (typeof entry.lifecycle?.unmount === 'function') {
        try {
          await entry.lifecycle.unmount(entry.context);
        } catch (error) {
          firstError ||= error;
        }
      }
    }
    if (disposeProvider && provider && typeof provider.unmount === 'function') {
      try {
        await provider.unmount();
      } catch (error) {
        firstError ||= error;
      }
    }
    if (disposeProvider) {
      providerGeneration += 1;
      provider = null;
      providerPromise = null;
    }
    if (firstError) throw firstError;
  }

  return Object.freeze({
    mountSurface,
    unmountSurface,
    unmountAll,
    hasMountedSurface(kind, descriptor) {
      return surfaces.has(surfaceKey(kind, descriptor));
    }
  });
}
